import React, { ComponentType, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Alert, Box, Container } from '@mui/material';

import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import { GHAPriceCheckContainer } from '~/components/GHAPriceCheck/GHAPriceCheckContainer';
import GHAPriceCompetitivenessContainer from '~/components/GHAPriceCompetitiveness/GHAPriceCompetitivenessContainer';
import HotelGroupView from '~/components/GoogleAdsHotelBidding/HotelGroupView';
import SimpleVoucherForm from '~/components/Vouchers/SimpleVoucherForm';

import CampaignLinksPage from '~/pages/CampaignLinks/CampaignLinksPage';
import AudiencesPage from '~/pages/Marketing/AudiencesPage';
import MarketingRouterRulesPage from '~/pages/Marketing/MarketingRouterRulesPage';
import MarketingRouterTimelinePage from '~/pages/Marketing/MarketingRouterTimelinePage';
import RegionRulePage from '~/pages/Marketing/RegionRulePage';
import VoucherAutoAssignRuleListPage from '~/pages/Vouchers/VoucherAutoAssignRuleListPage';
import VoucherFormPage from '~/pages/Vouchers/VoucherFormPage';

import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';
import tenants from '~/consts/tenant';

import useCurrentUser from '~/hooks/useCurrentUser';

import ChannelMarkupContainer from '../ChannelMarkup/ChannelMarkup';
import ChannelMarkupV2ListPage from '../ChannelMarkup/ChannelMarkupV2ListPage';
import PageHeader from '../Common/Elements/PageHeader';
import ContentfulFormsContainer from '../ContentfulForms/ContentfulFormsContainer';
import ForcedRankingsConfigContainer from '../ForcedRankingsConfig/ForcedRankingsConfig';
import GHADiscountingContainer from '../GHADiscounting/GHADiscountingContainer';
import GHAPropertyManagementContainer from '../GHAPropertyManagement/GHAPropertyManagementContainer';
import BlackhawkGiftCardLogsContainer from '../GiftCards/GiftCardLogs/BlackhawkGiftCardLogsContainer';
import GiftCardLogsContainer from '../GiftCards/GiftCardLogs/GiftCardLogsContainer';
import GiftCardsContainer from '../GiftCards/GiftCards/GiftCardsContainer';
import AdCreativeAIAssistantContainer from '../GoogleAds/AdCreativeAIAssistantContainer';
import BidModificationHistory from '../GoogleAds/BidModificationHistory';
import CustomerListView from '../GoogleAds/CustomerListView';
import GoogleAdsCampaignsConfigContainer from '../GoogleAdsCampaignsConfig/GoogleAdsCampaignsConfig';
import GoogleAdsRuleTemplatesContainer from '../GoogleAdsRuleTemplates/GoogleAdsRuleTemplatesContainer';
import HintCoverageMatrixContainer from '../HintCoverageMatrix/HintCoverageMatrixContainer';
import AgentVouchers from '../LeAgentHub/components/AgentVouchers/AgentVouchers';
import AgentCommissions from '../LeAgentHub/components/Commission/AgentCommissions';
import QuotedCommissions from '../LeAgentHub/components/Commission/QuotedCommissions';
import AgentQuotes from '../LeAgentHub/components/Quotes/AgentQuotes';
import PinnedOffersContainer from '../PinnedOffers/PinnedOffers';
import PinnedOffersConfigContainer from '../PinnedOffersConfig/PinnedOffersConfig';
import InAppMessageContainer from '../Promo/InAppMessageContainer';
import PromoRequestContainer from '../Promo/PromoRequests/PromoRequestContainer';
import PromoRoleManagement from '../Promo/PromoRoles/PromoRoleManagement';
import PromoSearchContainer from '../Promo/PromoSearchContainer';
import PromoTesterContainer from '../Promo/PromoTester/PromoTesterContainer';
import ReferralLogs from '../Promo/ReferralLogs';
import SimplePromoForm from '../Promo/SimplePromoForm';
import OfferMetricsContainer from '../PromoDisplayConfig/OfferMetricsContainer';
import PromoDisplayConfigContainer from '../PromoDisplayConfig/PromoDisplayConfig';
import LoyaltyPromoKeyOptionsContainer from '../PromoV3/LoyaltyPromoKeyOptionsContainer';
import PromoContainerV3 from '../PromoV3/PromoContainer';
import ReferralEarnOptionsContainer from '../ReferralEarnOptions/ReferralEarnOptionsContainer';
import Tags from '../Tags/Tags';
import MediaViewPage from '../Videos/MediaListPage';
import VoucherDisplayConfigContainer from '../VoucherDisplayConfig/VoucherDisplayConfig';
import VoucherUsersSearchPage from '../Vouchers/VoucherUsersSearchPage';
import VouchersContainer from '../Vouchers/Vouchers';

import BadgesContainer from './Badges/BadgesContainer';
import CompedPackagesContainer from './CompedPackages/CompedPackagesContainer';
import ContentSearchPage from './Content/ContentSearchPage';
import FacebookContentImagesContainer from './FacebookContentImages/FacebookContentImagesContainer';
import FacebookImageTemplatesContainer from './FacebookImageTemplates/FacebookImageTemplatesContainer';
import LeLiveContainer from './LeLive/LeLiveContainer';
import MarketingNavigation, { MarketingNavigationGroup } from './MarketingNavigation';
import OfferBlocklistContainer from './OfferBlocklist/OfferBlocklistContainer';
import OfferDeepLinksContainer from './OfferDeepLinks/OfferDeepLinksContainer';
import CampaignTags from './Tags';
import TemplatesContainer from './Templates/TemplatesContainer';

const CREATE_PROMO_TAB = 'create-promo';
const ADVANCED_PROMO_CREATE = 'advanced-create-promo';
const SEARCH_PROMO_TAB = 'search-promo';
const COMPED_TAB = 'comped';
const OFFER_DEEP_LINKS_TAB = 'offer-deep-links';

type TabConfig = {
  group: MarketingNavigationGroup;
  label: string;
  component: ComponentType<unknown>;
  tenants?: Set<string>;
  requiredRoles?: Array<string>;
};

function formatRequiredRoles(requiredRoles: Array<string>): string {
  return requiredRoles.join(', ');
}

export const tabsMap: Record<string, TabConfig> = {
  'agent-commissions': {
    group: 'Agent tools',
    label: 'Agent Commissions',
    component: AgentCommissions,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'quoted-commissions': {
    group: 'Agent tools',
    label: 'Quoted Commissions',
    component: QuotedCommissions,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'agent-vouchers': {
    group: 'Agent tools',
    label: 'Create Agent Voucher',
    component: AgentVouchers,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'agent-quotes': {
    group: 'Agent tools',
    label: 'Agent Quotes',
    component: AgentQuotes,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  [SEARCH_PROMO_TAB]: {
    group: 'Promotions',
    label: 'Search Promo Codes',
    component: PromoSearchContainer,
  },
  [CREATE_PROMO_TAB]: {
    group: 'Promotions',
    label: 'Simple Promo Create',
    component: SimplePromoForm,
  },
  [ADVANCED_PROMO_CREATE]: {
    group: 'Promotions',
    label: 'Advanced Promo Create',
    component: PromoContainerV3,
  },
  'promo-requests': {
    group: 'Promotions',
    label: 'Promo Requests',
    component: PromoRequestContainer,
  },
  'promo-tester': {
    group: 'Promotions',
    label: 'Promo Tester',
    component: PromoTesterContainer,
  },
  'promo-roles': {
    group: 'Promotions',
    label: 'Promo Roles',
    component: PromoRoleManagement,
    requiredRoles: [ROLE_PROMO_ADMIN_USER],
  },
  'loyalty-promo-keys': {
    group: 'Promotions',
    label: 'Promo Loyalty Reward Options',
    component: LoyaltyPromoKeyOptionsContainer,
  },
  'promo-display-config': {
    group: 'Promotions',
    label: 'Promo Display Config',
    component: PromoDisplayConfigContainer,
  },
  'offer-metrics': {
    group: 'Promotions',
    label: 'Offer Metrics',
    component: OfferMetricsContainer,
  },
  'in-app-messaging': {
    group: 'Promotions',
    label: 'In App Promo Messaging',
    component: InAppMessageContainer,
  },
  [COMPED_TAB]: {
    group: 'Promotions',
    label: 'Famils and competition winners',
    component: CompedPackagesContainer,
  },
  'campaign-tags': {
    group: 'Campaigns & audiences',
    label: 'Campaign Tags',
    component: CampaignTags,
  },
  'campaign-links': {
    group: 'Campaigns & audiences',
    label: 'Campaign Links',
    component: CampaignLinksPage,
  },
  tags: {
    group: 'Content & creative',
    label: 'Tags',
    component: Tags,
  },
  content: {
    group: 'Content & creative',
    label: 'Content',
    component: ContentSearchPage,
  },
  badges: {
    group: 'Content & creative',
    label: 'Badges',
    component: BadgesContainer,
  },
  templates: {
    group: 'Content & creative',
    label: 'Templates',
    component: TemplatesContainer,
  },
  [OFFER_DEEP_LINKS_TAB]: {
    group: 'Content & creative',
    label: 'Offer Deep Links',
    component: OfferDeepLinksContainer,
  },
  'gift-cards': {
    group: 'Gift cards',
    label: 'Gift Cards',
    component: GiftCardsContainer,
  },
  'gift-card-logs': {
    group: 'Gift cards',
    label: 'Gift Card Logs',
    component: GiftCardLogsContainer,
  },
  'blackhawk-gift-card-logs': {
    group: 'Gift cards',
    label: 'Blackhawk Logs',
    component: BlackhawkGiftCardLogsContainer,
  },
  'channel-markup': {
    group: 'Pricing & placement',
    label: 'Channel Markup',
    component: ChannelMarkupContainer,
  },
  'channel-markup-v2': {
    group: 'Pricing & placement',
    label: 'Channel Markup v2',
    component: ChannelMarkupV2ListPage,
  },
  'contentful-forms': {
    group: 'Content & creative',
    label: 'Forms',
    component: ContentfulFormsContainer,
  },
  'google-hotel-ads-property-management': {
    group: 'Google Ads',
    label: 'GHA Property Management',
    component: GHAPropertyManagementContainer,
  },
  // GHA-429: renamed in place (the tab key keeps existing bookmarks working) and given
  // sub-tabs: Summary / Scope Rules / Exclusions.
  'gha-property-discount-summary': {
    group: 'Google Ads',
    label: 'GHA Discounting',
    component: GHADiscountingContainer,
  },
  'google-hotel-ads-price-check': {
    group: 'Google Ads',
    label: 'GHA Price Comparison',
    component: GHAPriceCheckContainer,
  },
  'google-hotel-ads-price-competitiveness': {
    group: 'Google Ads',
    label: 'GHA Price Competitiveness',
    component: GHAPriceCompetitivenessContainer,
  },
  'hotel-bidding': {
    group: 'Google Ads',
    label: 'Hotel Bidding',
    component: HotelGroupView,
  },
  'bid-modification-history': {
    group: 'Google Ads',
    label: 'Bid Modification History',
    component: BidModificationHistory,
  },
  'google-ads-creative-ai': {
    group: 'Google Ads',
    label: 'Google Ads Creative AI',
    component: AdCreativeAIAssistantContainer,
  },
  'referral-earn-options': {
    group: 'Referrals',
    label: 'Referral Earn Options',
    component: ReferralEarnOptionsContainer,
  },
  referralLogs: {
    group: 'Referrals',
    label: 'Referral Logs',
    component: ReferralLogs,
  },
  'google-ads-automation': {
    group: 'Google Ads',
    label: 'Google Ads Automation',
    component: CustomerListView,
  },
  'google-ads-campaigns-config': {
    group: 'Google Ads',
    label: 'Google Ads Campaigns Config',
    component: GoogleAdsCampaignsConfigContainer,
  },
  'google-ads-rule-templates': {
    group: 'Google Ads',
    label: 'Google Ads Rule Templates',
    component: GoogleAdsRuleTemplatesContainer,
  },
  'pull-with-hints-coverage': {
    group: 'Pricing & placement',
    label: 'Pull With Hints Coverage',
    component: HintCoverageMatrixContainer,
  },
  'forced-rankings-config': {
    group: 'Pricing & placement',
    label: 'Forced Rankings Config',
    component: ForcedRankingsConfigContainer,
  },
  'pinned-offers-config': {
    group: 'Pricing & placement',
    label: 'Pinned Offers Config',
    component: PinnedOffersConfigContainer,
  },
  'pinned-offers': {
    group: 'Pricing & placement',
    label: 'Pinned Offers',
    component: PinnedOffersContainer,
  },
  'offer-blocklist': {
    group: 'Pricing & placement',
    label: 'Blocked Offers',
    component: OfferBlocklistContainer,
  },
  vouchers: {
    group: 'Vouchers',
    label: 'Vouchers',
    component: VouchersContainer,
  },
  'vouchers-create': {
    group: 'Vouchers',
    label: 'Create Voucher',
    component: VoucherFormPage,
  },
  'vouchers-simple-create': {
    group: 'Vouchers',
    label: 'Simple Voucher Create',
    component: SimpleVoucherForm,
  },
  'voucher-users': {
    group: 'Vouchers',
    label: 'Voucher Lookup',
    component: VoucherUsersSearchPage,
  },
  'voucher-display-config': {
    group: 'Vouchers',
    label: 'Voucher Display Config',
    component: VoucherDisplayConfigContainer,
  },
  'voucher-auto-assign-rules': {
    group: 'Vouchers',
    label: 'Voucher auto-assign rules',
    component: VoucherAutoAssignRuleListPage,
  },
  'video-gen': {
    group: 'Content & creative',
    label: 'Video Generator',
    component: MediaViewPage,
  },
  'facebook-image-templates': {
    group: 'Content & creative',
    label: 'Facebook Image Templates',
    component: FacebookImageTemplatesContainer,
  },
  'facebook-content-images': {
    group: 'Content & creative',
    label: 'Facebook Content Images',
    component: FacebookContentImagesContainer,
  },
  'le-live': {
    group: 'Content & creative',
    label: 'LE Live',
    component: LeLiveContainer,
  },
  'region-rule': {
    group: 'Campaigns & audiences',
    label: 'Region rules',
    component: RegionRulePage,
  },
  audiences: {
    group: 'Campaigns & audiences',
    label: 'Audiences',
    component: AudiencesPage,
  },
  'marketing-router-rules': {
    group: 'Campaigns & audiences',
    label: 'Marketing Router Rules',
    component: MarketingRouterRulesPage,
  },
  'marketing-router-timeline': {
    group: 'Campaigns & audiences',
    label: 'Router Timeline',
    component: MarketingRouterTimelinePage,
  },
} as const;

preRegisterSpotlightRoutes(expandTabRoutes('/marketing', 'Marketing', tabsMap));

export default function MarketingPage() {
  const { user } = useCurrentUser();
  const { tab } = useParams<'tab'>();
  const [showAllTabs, setShowAllTabs] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState<string | null>(null);
  const key = tab || SEARCH_PROMO_TAB;

  const currentTab = tabsMap[key];

  const brand = useSelector((state: App.State) => state.tenant);
  const userRoles = user?.roles ?? [];
  const hasRole = useMemo(() => {
    return (requiredRoles: Array<string>) => requiredRoles.some((role) => userRoles.includes(role));
  }, [userRoles]);
  const rolesLoading = false;

  const tabsList = useMemo(() => {
    return Object.keys(tabsMap).filter((key) => {
      const tab = tabsMap[key];

      if (tab.requiredRoles !== undefined) {
        return hasRole(tab.requiredRoles);
      }

      // For tenant-based filtering, always show
      if (!tab.tenants) {
        return true;
      }
      if (tab.tenants.has(brand.value)) {
        return true;
      }
      return false;
    });
  }, [brand, hasRole]);
  const visibleTabsList = showAllTabs ? Object.keys(tabsMap) : tabsList;

  const title = `Marketing / ${currentTab.label}`;

  const isCurrentTabAccessible = useMemo(() => {
    if (currentTab.requiredRoles !== undefined) {
      return hasRole(currentTab.requiredRoles);
    }

    return true;
  }, [currentTab, hasRole]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Marketing" />
        <SectionNavigationLayout
          navigation={
            <MarketingNavigation
              currentTab={key}
              items={visibleTabsList.map((id) => ({
                id,
                label: tabsMap[id].label,
                group: tabsMap[id].group,
                restricted: tabsMap[id].requiredRoles !== undefined && !hasRole(tabsMap[id].requiredRoles),
              }))}
              showAll={showAllTabs}
              onToggleShowAll={() => {
                setShowAllTabs((previous) => !previous);
                setPermissionMessage(null);
              }}
              onSelect={(event, id) => {
                const requiredRoles = tabsMap[id].requiredRoles;
                if (requiredRoles !== undefined && !hasRole(requiredRoles)) {
                  event.preventDefault();
                  setPermissionMessage(
                    `You need one of these roles to access this tab: ${formatRequiredRoles(requiredRoles)}`,
                  );
                  return;
                }
                setPermissionMessage(null);
                setShowAllTabs(false);
              }}
            />
          }
        >
          {permissionMessage ? (
            <Container maxWidth="xl" sx={{ mt: 2 }}>
              <Alert severity="error">{permissionMessage}</Alert>
            </Container>
          ) : null}

          {!rolesLoading &&
            !permissionMessage &&
            !isCurrentTabAccessible &&
            currentTab.requiredRoles?.includes(ROLE_PROMO_ADMIN_USER) && (
              <Container maxWidth="xl" sx={{ mt: 2 }}>
                <Alert severity="error">You need the {ROLE_PROMO_ADMIN_USER} role to access this tab.</Alert>
              </Container>
            )}

          {!rolesLoading &&
            !permissionMessage &&
            !isCurrentTabAccessible &&
            currentTab.requiredRoles !== undefined &&
            !currentTab.requiredRoles.includes(ROLE_PROMO_ADMIN_USER) && (
              <Container maxWidth="xl" sx={{ mt: 2 }}>
                <Alert severity="error">You don't have the role to access this tab.</Alert>
              </Container>
            )}

          {isCurrentTabAccessible && <Box component={currentTab.component} />}
        </SectionNavigationLayout>
      </Container>
    </>
  );
}
